// src/hooks/useGameState.ts
import { useState, useCallback } from "react";
import { Position, Piece } from "../types/Board.ts";
import { MoveValidator } from "../utils/moveValidator.ts";
import { MoveHistoryManager, Move, MoveAIContext } from "../utils/MoveHistoryManager.ts";
import { PIECE_DEFINITIONS } from "../data/pieceDefinitions.ts";
import {
  getSquareKey,
  getPieceAt,
  createBoardObject,
  isPromotionMove,
  createPromotedPiece,
} from "../utils/gameHelpers.ts";

export interface GameState {
  pieces: Map<string, Piece>;
  currentTurn: "white" | "black";
  gameStatus: "active" | "check" | "checkmate" | "stalemate";
  selectedSquare: Position | null;
  highlightedMoves: Position[];
  capturedPieces: { white: Piece[]; black: Piece[] };
  halfmoveClock: number;
  show50MoveWarning: boolean;
  historyPosition: number;
  isReviewingHistory: boolean;
  moveHistory: Array<Move & { moveNumber: string }>;
}

export interface PromotionState {
  showPromotionDialog: boolean;
  promotionMove: {
    from: Position;
    to: Position;
    capturedPiece?: Piece;
  } | null;
  availablePromotionPieces: string[];
}

interface UseGameStateProps {
  boardRows: number;
  boardCols: number;
  currentGameId: string;
  moveValidator: MoveValidator;
  historyManager: MoveHistoryManager;
  fiftyMoveLimit: number;
  seventyFiveMoveLimit: number;
  diagonalCaptureOnly?: boolean;
  onMoveComplete?: (
    piece: Piece,
    from: Position,
    to: Position,
    capturedPiece?: Piece,
    isCheck?: boolean,
    isCheckmate?: boolean
  ) => void;
  onGameEnd?: (
    result: "checkmate" | "stalemate",
    winner?: "white" | "black" | "draw"
  ) => void;
  // Optional callback for showing notifications instead of alerts
  onNotification?: (type: "success" | "error" | "info" | "warning", title: string, message: string) => void;
}

export const useGameState = ({
  boardRows,
  boardCols,
  currentGameId,
  moveValidator,
  historyManager,
  fiftyMoveLimit,
  seventyFiveMoveLimit,
  diagonalCaptureOnly = false,
  onMoveComplete,
  onGameEnd,
  onNotification,
}: UseGameStateProps) => {
  // Core game state
  const [pieces, setPieces] = useState<Map<string, Piece>>(new Map());
  const [currentTurn, setCurrentTurn] = useState<"white" | "black">("white");
  const [gameStatus, setGameStatus] = useState<
    "active" | "check" | "checkmate" | "stalemate"
  >("active");
  const [selectedSquare, setSelectedSquare] = useState<Position | null>(null);
  const [highlightedMoves, setHighlightedMoves] = useState<Position[]>([]);
  const [capturedPieces, setCapturedPieces] = useState<{
    white: Piece[];
    black: Piece[];
  }>({ white: [], black: [] });
  const [halfmoveClock, setHalfmoveClock] = useState<number>(0);
  const [show50MoveWarning, setShow50MoveWarning] = useState<boolean>(false);
  const [historyPosition, setHistoryPosition] = useState<number>(-1);
  const [isReviewingHistory, setIsReviewingHistory] = useState<boolean>(false);
  const [moveHistory, setMoveHistory] = useState<
    Array<Move & { moveNumber: string }>
  >([]);

  // Promotion state
  const [showPromotionDialog, setShowPromotionDialog] =
    useState<boolean>(false);
  const [promotionMove, setPromotionMove] = useState<{
    from: Position;
    to: Position;
    capturedPiece?: Piece;
  } | null>(null);
  const [availablePromotionPieces, setAvailablePromotionPieces] = useState<
    string[]
  >(["queen", "castle", "bishop", "knight"]);

  /**
   * Select a piece and show its legal moves, or deselect if already selected
   */
  const selectPiece = useCallback(
    (pos: Position, piece: Piece) => {
      // If clicking on the same square that's already selected, deselect it
      if (selectedSquare && selectedSquare.row === pos.row && selectedSquare.col === pos.col) {
        setSelectedSquare(null);
        setHighlightedMoves([]);
        return;
      }

      setSelectedSquare(pos);
      const board = createBoardObject(pieces, boardRows, boardCols, currentGameId);
      const legalMoves = moveValidator.getLegalMoves(piece, board, diagonalCaptureOnly);
      setHighlightedMoves(legalMoves);
    },
    [pieces, boardRows, boardCols, currentGameId, moveValidator, diagonalCaptureOnly, selectedSquare]
  );

  /**
   * Execute a move on the board
   * @param aiContext - Optional AI context for heuristics verification (only for AI moves)
   */
  const executeMove = useCallback(
    (
      from: Position,
      to: Position,
      capturedPiece?: Piece,
      promotionPieceType?: string,
      aiContext?: MoveAIContext
    ) => {
      const piece = getPieceAt(from.row, from.col, pieces);
      if (!piece) return;

      const newPieces = new Map(pieces);
      const board = createBoardObject(pieces, boardRows, boardCols, currentGameId);

      const validation = moveValidator.validateMove(piece, from, to, board, diagonalCaptureOnly);

      let isEnPassant = false;
      let enPassantCaptureSquare: Position | undefined;
      let actualCapturedPiece = capturedPiece;
      let finalPiece = piece;

      const isPawnMove =
        piece.type.toLowerCase() === "pawn" ||
        piece.type.toLowerCase() === "knave" ||
        piece.type.toLowerCase() === "squire";
      const isCapture = !!capturedPiece;

      // Handle promotion
      if (promotionPieceType && isPromotionMove(piece, to.row, boardRows)) {
        const promotedPiece = createPromotedPiece(piece, promotionPieceType, to);
        if (!promotedPiece) {
          if (onNotification) {
            onNotification("error", "Promotion Error", `Cannot promote to ${promotionPieceType}. Piece definition not found.`);
          }
          return;
        }
        finalPiece = promotedPiece;
      }

      // Handle en passant
      if (
        piece.type.toLowerCase() === "pawn" &&
        !capturedPiece &&
        Math.abs(to.col - from.col) === 1
      ) {
        if (
          historyManager.isEnPassantLegal(
            from.row,
            from.col,
            to.row,
            to.col,
            piece.color
          )
        ) {
          isEnPassant = true;
          const enPassantState = historyManager.getEnPassantState();
          const capturedPawnRow = enPassantState.capturedPawnRank!;
          const capturedPawnCol = enPassantState.capturedPawnFile!;
          enPassantCaptureSquare = { row: capturedPawnRow, col: capturedPawnCol };

          actualCapturedPiece = getPieceAt(capturedPawnRow, capturedPawnCol, pieces);
          newPieces.delete(getSquareKey(capturedPawnRow, capturedPawnCol));
        }
      }

      // Handle castling
      if (
        validation.isCastling &&
        validation.castlingRookFrom &&
        validation.castlingRookTo
      ) {
        newPieces.delete(getSquareKey(from.row, from.col));
        const movedKing: Piece = {
          ...piece,
          position: to,
          hasMoved: true,
        };
        newPieces.set(getSquareKey(to.row, to.col), movedKing);

        const rook = getPieceAt(
          validation.castlingRookFrom.row,
          validation.castlingRookFrom.col,
          pieces
        );
        if (rook) {
          newPieces.delete(
            getSquareKey(
              validation.castlingRookFrom.row,
              validation.castlingRookFrom.col
            )
          );
          const movedRook: Piece = {
            ...rook,
            position: validation.castlingRookTo,
            hasMoved: true,
          };
          newPieces.set(
            getSquareKey(
              validation.castlingRookTo.row,
              validation.castlingRookTo.col
            ),
            movedRook
          );
        }
      } else {
        // Normal move
        newPieces.delete(getSquareKey(from.row, from.col));
        const movedPiece: Piece = {
          ...finalPiece,
          position: to,
          hasMoved: true,
        };
        newPieces.set(getSquareKey(to.row, to.col), movedPiece);
      }

      // Update captured pieces
      if (actualCapturedPiece) {
        setCapturedPieces((prev) => ({
          ...prev,
          [actualCapturedPiece.color]: [
            ...prev[actualCapturedPiece.color],
            actualCapturedPiece,
          ],
        }));
      }

      setPieces(newPieces);

      const nextTurn = currentTurn === "white" ? "black" : "white";

      // Create updated board for check/checkmate detection
      const updatedBoard = {
        dimensions: { rows: boardRows, cols: boardCols },
        squares: Array(boardRows)
          .fill(null)
          .map(() => Array(boardCols).fill(null)),
        piecesMap: newPieces,
        metadata: { variantId: currentGameId, createdAt: new Date() },
      };

      newPieces.forEach((p, key) => {
        const [r, c] = key.split("-").map(Number);
        updatedBoard.squares[r][c] = p;
      });

      // Check if a royal piece (King or Emperor) was captured - this is immediate game over
      const isRoyalPieceCapture = actualCapturedPiece &&
        (actualCapturedPiece.type.toLowerCase() === 'king' ||
         actualCapturedPiece.type.toLowerCase() === 'emperor');

      // Check game status
      let isCheck, isCheckmate, isStalemate;

      if (isRoyalPieceCapture) {
        // Royal piece captured = immediate checkmate (capturing player wins)
        isCheck = false;
        isCheckmate = true;
        isStalemate = false;
        console.log(`[GameState] Royal piece captured: ${actualCapturedPiece?.type} (${actualCapturedPiece?.color}) - Game Over!`);
      } else {
        try {
          isCheck = moveValidator.isKingInCheck(
            newPieces,
            nextTurn,
            boardRows,
            boardCols
          );
          isCheckmate =
            isCheck && moveValidator.isCheckmate(updatedBoard, nextTurn);
          isStalemate =
            !isCheck && moveValidator.isStalemate(updatedBoard, nextTurn);
        } catch (error) {
          console.error("Error during check/checkmate validation:", error);
          throw error;
        }
      }

      // Add move to history (with AI context if this was an AI move)
      historyManager.addMove(
        piece,
        from,
        to,
        actualCapturedPiece,
        isCheck,
        isCheckmate,
        isEnPassant,
        enPassantCaptureSquare,
        promotionPieceType,
        aiContext
      );

      const formattedHistory = historyManager.getMovesWithNumbers();
      setMoveHistory(formattedHistory);

      // Update halfmove clock
      let newHalfmoveClock = halfmoveClock;
      if (isPawnMove || isCapture || isEnPassant) {
        newHalfmoveClock = 0;
      } else {
        newHalfmoveClock = halfmoveClock + 1;
      }
      setHalfmoveClock(newHalfmoveClock);

      // Check for automatic draw by 75-move rule
      const autoDrawThreshold = seventyFiveMoveLimit * 2;
      if (newHalfmoveClock >= autoDrawThreshold) {
        setGameStatus("stalemate");
        onGameEnd?.("stalemate", "draw");
        if (onNotification) {
          onNotification("info", "Draw", `Draw by ${seventyFiveMoveLimit}-move rule! No capture or pawn move in ${seventyFiveMoveLimit} consecutive moves by each player.`);
        }
      } else if (isCheckmate) {
        setGameStatus("checkmate");
        // Winner is the current player who made the checkmating move (or captured the royal piece)
        const winner = currentTurn;
        onGameEnd?.("checkmate", winner);
      } else if (isStalemate) {
        setGameStatus("stalemate");
        onGameEnd?.("stalemate", "draw");
      } else if (isCheck) {
        setGameStatus("check");
      } else {
        setGameStatus("active");

        // Show 50-move draw warning
        const claimDrawThreshold = fiftyMoveLimit * 2;
        if (newHalfmoveClock >= claimDrawThreshold) {
          setShow50MoveWarning(true);
        }
      }

      // Update turn
      setCurrentTurn(nextTurn);
      setSelectedSquare(null);
      setHighlightedMoves([]);
      setHistoryPosition(-1);
      setIsReviewingHistory(false);

      // Notify parent component
      onMoveComplete?.(
        piece,
        from,
        to,
        actualCapturedPiece,
        isCheck,
        isCheckmate
      );
    },
    [
      pieces,
      boardRows,
      boardCols,
      currentGameId,
      currentTurn,
      halfmoveClock,
      fiftyMoveLimit,
      seventyFiveMoveLimit,
      moveValidator,
      historyManager,
      onMoveComplete,
      onGameEnd,
    ]
  );

  /**
   * Handle a move request - checks for promotion first
   * @param aiContext - Optional AI context for heuristics verification (only for AI moves)
   */
  const movePiece = useCallback(
    (from: Position, to: Position, capturedPiece?: Piece, aiContext?: MoveAIContext) => {
      const piece = getPieceAt(from.row, from.col, pieces);
      if (!piece) return;

      // Check if this is a promotion move
      if (isPromotionMove(piece, to.row, boardRows)) {
        setPromotionMove({ from, to, capturedPiece });
        setShowPromotionDialog(true);
        // Note: AI context will be lost for promotion moves - AI should handle its own promotions
        return;
      }

      // Execute non-promotion move (with AI context if provided)
      executeMove(from, to, capturedPiece, undefined, aiContext);
    },
    [pieces, boardRows, executeMove]
  );

  /**
   * Handle promotion piece selection
   */
  const handlePromotion = useCallback(
    (promotionPieceType: string) => {
      if (!promotionMove) return;

      setShowPromotionDialog(false);
      executeMove(
        promotionMove.from,
        promotionMove.to,
        promotionMove.capturedPiece,
        promotionPieceType
      );
      setPromotionMove(null);
    },
    [promotionMove, executeMove]
  );

  /**
   * Reset game to initial state
   * @param keepPieces - If true, preserves the current pieces on the board (useful when loading a new preset)
   */
  const resetGame = useCallback((keepPieces = false) => {
    // Only clear pieces if keepPieces is false
    if (!keepPieces) {
      setPieces(new Map());
    }
    
    setCurrentTurn("white");
    setGameStatus("active");
    setSelectedSquare(null);
    setHighlightedMoves([]);
    setCapturedPieces({ white: [], black: [] });
    setHalfmoveClock(0);
    setShow50MoveWarning(false);
    setHistoryPosition(-1);
    setIsReviewingHistory(false);
    setMoveHistory([]);
    historyManager.clear();
  }, [historyManager]);

  /**
   * Set pieces directly (used for board initialization)
   */
  const setGamePieces = useCallback((newPieces: Map<string, Piece>) => {
    setPieces(newPieces);
  }, []);

  /**
   * Set available promotion pieces
   */
  const setPromotionPieces = useCallback((pieces: string[]) => {
    setAvailablePromotionPieces(pieces);
  }, []);

  return {
    // State
    gameState: {
      pieces,
      currentTurn,
      gameStatus,
      selectedSquare,
      highlightedMoves,
      capturedPieces,
      halfmoveClock,
      show50MoveWarning,
      historyPosition,
      isReviewingHistory,
      moveHistory,
    },
    promotionState: {
      showPromotionDialog,
      promotionMove,
      availablePromotionPieces,
    },
    
    // Actions
    selectPiece,
    movePiece,
    executeMove,
    handlePromotion,
    resetGame,
    setGamePieces,
    setPromotionPieces,
    
    // State setters (for external control)
    setCurrentTurn,
    setGameStatus,
    setSelectedSquare,
    setHighlightedMoves,
    setCapturedPieces,
    setHalfmoveClock,
    setShow50MoveWarning,
    setHistoryPosition,
    setIsReviewingHistory,
    setMoveHistory,
    setShowPromotionDialog,
    setPromotionMove,
  };
};